import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import PharmeasyLayout from "@/components/PharmeasyLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  ArrowLeft, Navigation, MapPin, MapPinned, List, Map as MapIcon,
  Clock, Calendar, ChevronLeft, ChevronRight
} from "lucide-react";

const RiderLocation = ({ user, onLogout }) => {
  const { riderId } = useParams();
  const navigate = useNavigate();
  
  const [rider, setRider] = useState(null);
  const [locationHistory, setLocationHistory] = useState([]);
  const [viewMode, setViewMode] = useState("list"); // "list" or "map"
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Fetch rider details
  useEffect(() => {
    const fetchRider = async () => {
      try {
        const res = await axios.get(`${API}/pharmeasy-orders/riders/${riderId}`);
        setRider(res.data);
      } catch (e) {
        toast.error("Failed to fetch rider details");
      } finally {
        setLoading(false);
      }
    };
    fetchRider();
  }, [riderId]);

  // Fetch location history when date changes
  useEffect(() => {
    const fetchHistory = async () => {
      if (!riderId) return;
      setLoadingHistory(true);
      try {
        const res = await axios.get(`${API}/pharmeasy-orders/riders/${riderId}/location-history`, {
          params: { date: selectedDate }
        });
        setLocationHistory(res.data.locations || []);
      } catch (e) {
        toast.error("Failed to fetch location history");
        setLocationHistory([]);
      } finally {
        setLoadingHistory(false);
      }
    };
    fetchHistory();
  }, [riderId, selectedDate]);

  const formatTime = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-IN", { timeStyle: "medium" }); }
    catch { return iso; }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  const getMapUrl = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;

  const changeDate = (days) => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + days);
    setSelectedDate(current.toISOString().split('T')[0]);
  };

  if (loading) {
    return (
      <PharmeasyLayout user={user} onLogout={onLogout}>
        <div className="p-8 text-center text-slate-600">Loading...</div>
      </PharmeasyLayout>
    );
  }

  return (
    <PharmeasyLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="rider-location-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/pharmeasy/riders")} className="p-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Rider Location</h1>
            <p className="text-slate-600">{rider?.name} • {rider?.mobile}</p>
          </div>
        </div>

        {/* Live Location Card */}
        {rider?.last_location && (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <MapPinned className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-green-800 text-lg">Live Location</p>
                    <p className="text-sm text-green-600">
                      Last updated: {formatDate(rider.last_location?.timestamp)}
                    </p>
                  </div>
                </div>
                <a 
                  href={getMapUrl(rider.last_location?.latitude, rider.last_location?.longitude)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                >
                  <MapIcon className="w-4 h-4" />
                  Open in Maps
                </a>
              </div>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white rounded-lg p-4">
                  <p className="text-xs text-slate-500">Latitude</p>
                  <p className="font-mono text-lg">{rider.last_location?.latitude?.toFixed(6) || "—"}</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-xs text-slate-500">Longitude</p>
                  <p className="font-mono text-lg">{rider.last_location?.longitude?.toFixed(6) || "—"}</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-xs text-slate-500">Accuracy</p>
                  <p className="font-mono text-lg">{rider.last_location?.accuracy ? `${rider.last_location.accuracy}m` : "—"}</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-xs text-slate-500">Speed</p>
                  <p className="font-mono text-lg">{rider.last_location?.speed ? `${rider.last_location.speed} km/h` : "—"}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Historical Data Section */}
        <Card className="border-0 shadow-lg">
          <CardContent className="pt-5">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
                <Clock className="w-5 h-5 text-slate-500" />
                Location History
              </h2>
              <div className="flex items-center gap-4">
                {/* Date Selector */}
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="sm" onClick={() => changeDate(-1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Input 
                    type="date" 
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-44"
                  />
                  <Button variant="outline" size="sm" onClick={() => changeDate(1)} disabled={selectedDate >= new Date().toISOString().split('T')[0]}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
                {/* View Toggle */}
                <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
                  <Button 
                    variant={viewMode === "list" ? "default" : "ghost"} 
                    size="sm"
                    onClick={() => setViewMode("list")}
                    className={viewMode === "list" ? "" : "hover:bg-slate-200"}
                  >
                    <List className="w-4 h-4 mr-1" /> List
                  </Button>
                  <Button 
                    variant={viewMode === "map" ? "default" : "ghost"} 
                    size="sm"
                    onClick={() => setViewMode("map")}
                    className={viewMode === "map" ? "" : "hover:bg-slate-200"}
                  >
                    <MapIcon className="w-4 h-4 mr-1" /> Map
                  </Button>
                </div>
              </div>
            </div>

            {/* Content */}
            {loadingHistory ? (
              <div className="p-8 text-center text-slate-500">Loading location history...</div>
            ) : locationHistory.length === 0 ? (
              <div className="p-12 text-center">
                <MapPin className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-lg text-slate-500">No location data for {selectedDate}</p>
                <p className="text-sm text-slate-400">Try selecting a different date</p>
              </div>
            ) : viewMode === "list" ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">#</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Time</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Type</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Latitude</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Longitude</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Accuracy</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Map</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {locationHistory.map((loc, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="px-4 py-3 align-middle text-sm text-slate-500">{idx + 1}</td>
                        <td className="px-4 py-3 align-middle font-mono text-sm text-slate-700">{formatTime(loc.timestamp)}</td>
                        <td className="px-4 py-3 align-middle">
                          <Badge variant="outline" className={
                            loc.type === "CHECK_IN" ? "bg-green-50 text-green-700" :
                            loc.type === "CHECK_OUT" ? "bg-red-50 text-red-700" :
                            "bg-blue-50 text-blue-700"
                          }>
                            {loc.type?.replace("_", " ") || "UPDATE"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 align-middle text-right font-mono text-sm text-slate-600">{loc.latitude?.toFixed(6)}</td>
                        <td className="px-4 py-3 align-middle text-right font-mono text-sm text-slate-600">{loc.longitude?.toFixed(6)}</td>
                        <td className="px-4 py-3 align-middle text-right text-sm text-slate-500">{loc.accuracy ? `${loc.accuracy}m` : "—"}</td>
                        <td className="px-4 py-3 align-middle text-center">
                          <a 
                            href={getMapUrl(loc.latitude, loc.longitude)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Navigation className="w-4 h-4 inline" />
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="p-4 text-sm text-slate-500 border-t">
                  Total: {locationHistory.length} location updates
                </div>
              </div>
            ) : (
              <div className="rounded-lg overflow-hidden border" style={{ height: "500px" }}>
                {locationHistory.length > 0 && (
                  <iframe 
                    title="Location History Map"
                    width="100%" 
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    src={`https://www.google.com/maps/embed/v1/directions?key=${process.env.REACT_APP_GOOGLE_MAPS_KEY}&origin=${locationHistory[0]?.latitude},${locationHistory[0]?.longitude}&destination=${locationHistory[locationHistory.length-1]?.latitude},${locationHistory[locationHistory.length-1]?.longitude}${locationHistory.length > 2 ? `&waypoints=${locationHistory.slice(1, Math.min(locationHistory.length-1, 23)).map(l => `${l.latitude},${l.longitude}`).join("|")}` : ""}&mode=driving`}
                  />
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PharmeasyLayout>
  );
};

export default RiderLocation;
